/* eslint-disable no-nested-ternary */
import {
  differenceInDays,
  subDays,
  addDays,
  addWeeks,
  addMonths,
  addYears,
  differenceInCalendarDays,
  isLeapYear,
  isWithinInterval,
  startOfDay,
  isSameDay,
  parseISO,
  max,
} from 'date-fns';
import { BOOKING_OTB_PARTIAL_HOURS_THRESHOLD, OFFSET, TIME_UNIT } from '../../dashboard/constants';
import { compareWindows } from '../ui-controller';

/**
 * Checks whether a given date range includes February 29 (leap day).
 * @param {string | Date} startDate - The start date of the range
 * @param {string | Date} endDate - The end date of the range
 * @returns {boolean} Returns `true` if the range includes February 29
 */
export const hasLeapDay = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  //
  for (let year = start.getFullYear(); year <= end.getFullYear(); year += 1) {
    if (isLeapYear(new Date(year, 0, 1))) {
      const feb29 = new Date(year, 1, 29);
      if (isWithinInterval(feb29, { start, end })) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Calculates the custom comparison period for weekly alignment cases.
 * The custom comparison period is first determined based on the user's original selected dates.
 * Once the selected stay-date range is adjusted to the Sunday–Saturday weekly range, the offset
 * between the original selected end date and the custom comparison end date is applied to the
 * adjusted range to determine the new custom comparison period.
 * @param {{ startDate: string | Date, endDate: string | Date }} adjustedStayDate - The weekly adjusted stay-date range
 * @param {string | Date} originalEndDate - The end date of the user's original selected stay-date range
 * @param {{ startDate: string | Date, endDate: string | Date }} customDate - The original custom comparison period
 * @returns {{ startDate: Date, endDate: Date }} The adjusted custom comparison period
 */
export const getWeeklyAlignedCustomDate = (adjustedStayDate, originalEndDate, customDate) => {
  if (!customDate?.endDate || !originalEndDate) return customDate;
  const offsetDays = differenceInCalendarDays(
    startOfDay(new Date(originalEndDate)),
    startOfDay(new Date(customDate.endDate))
  );

  return {
    startDate: subDays(startOfDay(new Date(adjustedStayDate.startDate)), offsetDays),
    endDate: subDays(startOfDay(new Date(adjustedStayDate.endDate)), offsetDays),
  };
};

/**
 * Applies an offset to a date based on the specified time unit, used for booking OTB adjustments.
 * @param {string} unit - The time unit for the offset ('year', 'month', 'week')
 * @param {Date} date - The date to which the offset will be applied
 * @return {Date|null} The adjusted date after applying the offset, or null if the unit is invalid
 */
const applyBookingOtbOffset = (unit, date) => {
  switch (unit) {
    case TIME_UNIT.YEAR:
      return addYears(date, OFFSET.BOOKING_OTB);
    case TIME_UNIT.MONTH:
      return addMonths(date, OFFSET.BOOKING_OTB);
    case TIME_UNIT.WEEK:
      return addWeeks(date, OFFSET.BOOKING_OTB);
    default:
      return null;
  }
};

/**
 * Shifts a date range by a specified offset in a given unit, normalized to date-only precision.
 * @param {string | Date} startDate
 * @param {string | Date} endDate
 * @param {'year'|'month'|'week'|'day'} unit
 * @param {number} offset
 * @param {boolean} align
 * @param {boolean} strictMonthEnd - Prevents unaligned monthly shifts from losing a day at month end
 * @returns {{ startDate: string, endDate: string }}
 */
const adjustRange = ({
  resetBookingDate,
  startDate,
  endDate,
  unit,
  offset,
  align = false,
  symmetricComparisonToggle,
  lastBookingDate,
  partialHours,
  isBookingDate = false,
  strictMonthEnd = false,
}) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  //
  const applyOffset = (date) => {
    if (align) return addDays(date, offset);
    switch (unit) {
      case TIME_UNIT.YEAR:
        return addYears(date, offset);
      case TIME_UNIT.MONTH:
        return addMonths(date, offset);
      case TIME_UNIT.WEEK:
        return addWeeks(date, offset);
      default:
        return date;
    }
  };
  //
  const isOtb =
    isSameDay(new Date(startDate), new Date(resetBookingDate?.startDate)) &&
    isSameDay(new Date(endDate), startOfDay(parseISO(resetBookingDate?.endDate)));
  //
  const adjustedStart = applyOffset(start);
  let adjustedEnd = subDays(applyOffset(addDays(end, 1)), 1);
  // Month-end clamping of addMonths (e.g. Dec 31 -> Nov 30) drops a day on unaligned
  // monthly shifts; never return an end bound before the strict month shift of the end date.
  if (strictMonthEnd && !align && unit === TIME_UNIT.MONTH) {
    adjustedEnd = max([adjustedEnd, applyOffset(end)]);
  }
  // For booking date adjustments in symmetric comparison & OTB, apply an additional offset based on the duration of the booking period.
  if (isBookingDate && symmetricComparisonToggle && isOtb) {
    if (partialHours > BOOKING_OTB_PARTIAL_HOURS_THRESHOLD) {
      adjustedEnd =
        applyBookingOtbOffset(unit, startOfDay(parseISO(lastBookingDate))) || adjustedEnd;
    } else {
      adjustedEnd =
        applyBookingOtbOffset(unit, subDays(startOfDay(parseISO(lastBookingDate)), 1)) ||
        adjustedEnd;
    }
  }
  //
  return {
    startDate: adjustedStart,
    endDate: adjustedEnd,
  };
};

/**
 * Calculates adjusted comparison date ranges for stay and booking periods.
 * @param {{ startDate: string, endDate: string }} stayDate
 * @param {{ startDate: string, endDate: string }} bookingDate
 * @param {boolean} alignmentToggle
 * @param {boolean} symmetricComparisonToggle
 * @param {string} dateComparison
 * @param {Object} [options]
 * @param {boolean} [options.strictMonthEnd=false] - When true, previous month unaligned comparison
 * end dates are never moved before the selected end date minus one month (used by Pace JS visuals)
 * @returns {{ adjustedStayDate: { startDate: string, endDate: string }, adjustedBookingDate: { startDate: string, endDate: string } }}
 */
const calculateComparisonDates = (
  stayDate,
  bookingDate,
  alignmentToggle,
  symmetricComparisonToggle,
  dateComparison,
  customDate,
  resetBooking,
  partialHours,
  lastBookingDate,
  { strictMonthEnd = false } = {}
) => {
  const stayStart = startOfDay(new Date(stayDate.startDate));
  const stayEnd = startOfDay(new Date(stayDate.endDate));
  const bookingStart = startOfDay(new Date(bookingDate.startDate));
  const bookingEnd = startOfDay(new Date(bookingDate.endDate));
  //
  let adjustedStayDate = {
    startDate: stayStart,
    endDate: stayEnd,
  };
  //
  let adjustedBookingDate = {
    startDate: bookingStart,
    endDate: bookingEnd,
  };
  //
  const resetBookingDate = {
    startDate: new Date(resetBooking.startDate),
    endDate: startOfDay(parseISO(resetBooking.endDate)),
    key: 'selection',
  };
  //
  const isOtb =
    isSameDay(new Date(bookingStart), new Date(resetBookingDate?.startDate)) &&
    isSameDay(new Date(bookingEnd), startOfDay(parseISO(resetBookingDate?.endDate)));
  //
  switch (dateComparison) {
    case compareWindows.py: {
      adjustedStayDate = alignmentToggle
        ? adjustRange({
            startDate: stayStart,
            endDate: stayEnd,
            unit: TIME_UNIT.DAY,
            offset: OFFSET.ALIGN_DAY,
            align: true,
            symmetricComparisonToggle,
          })
        : adjustRange({
            startDate: stayStart,
            endDate: stayEnd,
            unit: TIME_UNIT.YEAR,
            offset: OFFSET.YEAR,
            symmetricComparisonToggle,
          });
      //
      adjustedBookingDate = symmetricComparisonToggle
        ? alignmentToggle
          ? adjustRange({
              resetBookingDate,
              startDate: bookingStart,
              endDate: bookingEnd,
              unit: TIME_UNIT.DAY,
              offset: OFFSET.ALIGN_DAY,
              align: true,
              symmetricComparisonToggle,
              isBookingDate: true,
              partialHours,
              lastBookingDate,
            })
          : adjustRange({
              resetBookingDate,
              startDate: bookingStart,
              endDate: bookingEnd,
              unit: TIME_UNIT.YEAR,
              offset: OFFSET.YEAR,
              symmetricComparisonToggle,
              isBookingDate: true,
              partialHours,
              lastBookingDate,
            })
        : resetBookingDate;
      break;
    }
    //
    case compareWindows.pm: {
      adjustedStayDate = alignmentToggle
        ? adjustRange({
            resetBookingDate,
            startDate: stayStart,
            endDate: stayEnd,
            unit: TIME_UNIT.DAY,
            offset: OFFSET.ALIGN_MONTH_DAY,
            align: true,
            symmetricComparisonToggle,
          })
        : adjustRange({
            resetBookingDate,
            startDate: stayStart,
            endDate: stayEnd,
            unit: TIME_UNIT.MONTH,
            offset: OFFSET.MONTH,
            symmetricComparisonToggle,
            strictMonthEnd,
          });
      if (symmetricComparisonToggle) {
        if (alignmentToggle) {
          adjustedBookingDate = adjustRange({
            resetBookingDate,
            startDate: bookingStart,
            endDate: bookingEnd,
            unit: TIME_UNIT.DAY,
            offset: OFFSET.ALIGN_MONTH_DAY,
            align: true,
            symmetricComparisonToggle,
            isBookingDate: true,
            partialHours,
            lastBookingDate,
          });
        } else {
          const bookingDuration = differenceInDays(bookingEnd, bookingStart);
          const gap = differenceInDays(stayEnd, bookingEnd);
          let maxDate = subDays(adjustedStayDate.endDate, gap);
          if (isOtb) {
            // For OTB comparisons, adjust the maxDate based on the partial hours and last booking date.
            if (partialHours > 12) {
              maxDate =
                applyBookingOtbOffset(TIME_UNIT.MONTH, startOfDay(parseISO(lastBookingDate))) ||
                maxDate;
            } else {
              maxDate =
                applyBookingOtbOffset(
                  subDays(TIME_UNIT.MONTH, startOfDay(parseISO(lastBookingDate)), 1)
                ) || maxDate;
            }
          }
          const minDate = subDays(maxDate, bookingDuration);
          //
          adjustedBookingDate = {
            startDate: minDate,
            endDate: maxDate,
          };
        }
      } else {
        adjustedBookingDate = resetBookingDate;
      }
      break;
    }
    //
    case compareWindows.pw: {
      adjustedStayDate = adjustRange({
        resetBookingDate,
        startDate: stayStart,
        endDate: stayEnd,
        unit: TIME_UNIT.WEEK,
        offset: OFFSET.WEEK,
        symmetricComparisonToggle,
      });
      //
      adjustedBookingDate = symmetricComparisonToggle
        ? adjustRange({
            resetBookingDate,
            startDate: bookingStart,
            endDate: bookingEnd,
            unit: TIME_UNIT.WEEK,
            offset: OFFSET.WEEK,
            symmetricComparisonToggle,
            isBookingDate: true,
            partialHours,
            lastBookingDate,
          })
        : resetBookingDate;
      break;
    }
    //
    case compareWindows.otb: {
      return {
        adjustedStayDate,
        adjustedBookingDate: resetBookingDate,
      };
    }
    //
    case compareWindows.cus: {
      adjustedStayDate = {
        startDate: new Date(customDate?.startDate),
        endDate: new Date(customDate?.endDate),
      };
      if (symmetricComparisonToggle) {
        const intervalInDays = differenceInCalendarDays(stayEnd, new Date(customDate?.endDate));
        //
        adjustedBookingDate = {
          startDate: subDays(bookingStart, intervalInDays),
          endDate: subDays(bookingEnd, intervalInDays),
        };
      } else {
        adjustedBookingDate = resetBookingDate;
      }
      break;
    }
    default:
      break;
  }
  //
  return { adjustedStayDate, adjustedBookingDate };
};
//
export default calculateComparisonDates;
